import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ExternalLink } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import ActiviteFiltersBar from '../components/activite/ActiviteFiltersBar';
import ActiviteTabBar from '../components/activite/ActiviteTabBar';
import ActiviteShellTab from '../components/activite/tabs/ActiviteShellTab';
import ActiviteVentesTab from '../components/activite/tabs/ActiviteVentesTab';
import ActivitePaiementsTab from '../components/activite/tabs/ActivitePaiementsTab';
import ActiviteCaisseTab from '../components/activite/tabs/ActiviteCaisseTab';
import ActiviteStockTab from '../components/activite/tabs/ActiviteStockTab';
import ActiviteReparationsTab from '../components/activite/tabs/ActiviteReparationsTab';
import ActiviteOverviewTab from '../components/activite/tabs/ActiviteOverviewTab';
import ActiviteEquipeTab from '../components/activite/tabs/ActiviteEquipeTab';
import useActiviteFilters from '../hooks/activite/useActiviteFilters';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { isPrimaryAdmin } from '../utils/roleRouting';
import { ACTIVITE_TAB_IDS, ACTIVITE_TIMELINE_MVP_LIMIT } from '../utils/activite/activityTabs';

const ActivitePage = () => {
  const { t } = useLanguage();
  const { user, isAdmin } = useAuth();
  const isCarlosOnly = isPrimaryAdmin(user);

  const {
    tab,
    filters,
    visibleTabs,
    setTab,
    setSearch,
    setMagasinId,
    setVendeur,
    setPeriod,
    setDateRange,
  } = useActiviteFilters({ isAdmin, isCarlosOnly });

  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === tab)) {
      setTab(visibleTabs[0]?.id);
    }
  }, [visibleTabs, tab, setTab]);

  return (
    <Layout>
      <div className="min-h-screen bg-[#0f1117] text-white pb-24 lg:pb-6" data-testid="activite-page-root">
      <div className="max-w-7xl mx-auto fade-in pb-6 px-1 sm:px-0" data-testid="activite-page">
        <PageHeader
          title={t('activite.pageTitle')}
          subtitle={t('activite.pageSubtitle')}
          icon={Activity}
          testId="activite-page-header"
          actions={(
            <Link
              to="/historique-ventes"
              className="btn-secondary px-4 py-2 min-h-[44px] text-sm inline-flex items-center gap-2 border border-white/10 bg-[#151820] text-gray-300 hover:text-white rounded-lg"
              data-testid="activite-link-historique"
            >
              <ExternalLink className="w-4 h-4" />
              {t('activite.historiqueDetaille')}
            </Link>
          )}
        />

        <ActiviteFiltersBar
          filters={filters}
          isAdmin={isAdmin}
          onSearchChange={setSearch}
          onPeriodChange={setPeriod}
          onMagasinChange={setMagasinId}
          onVendeurChange={setVendeur}
          onDateRangeChange={setDateRange}
        />

        <ActiviteTabBar tabs={visibleTabs} activeTab={tab} onTabChange={setTab} />

        <div role="tabpanel" className="mt-2" data-testid="activite-tab-panel">
          {tab === ACTIVITE_TAB_IDS.OVERVIEW ? (
            <ActiviteOverviewTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.VENTES ? (
            <ActiviteVentesTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.PAIEMENTS ? (
            <ActivitePaiementsTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.CAISSE ? (
            <ActiviteCaisseTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.STOCK ? (
            <ActiviteStockTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.REPARATIONS ? (
            <ActiviteReparationsTab filters={filters} />
          ) : tab === ACTIVITE_TAB_IDS.EQUIPE ? (
            <ActiviteEquipeTab filters={filters} />
          ) : (
            <ActiviteShellTab tabId={tab} />
          )}
        </div>

        {tab === ACTIVITE_TAB_IDS.OVERVIEW && (
          <span className="sr-only" data-testid="activite-timeline-mvp-limit">
            {ACTIVITE_TIMELINE_MVP_LIMIT}
          </span>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default ActivitePage;
